import {find, head, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {BankAccountResponseBody} from '@dms/api/shared';

export type BankAccountDomain = NonNullable<BankAccountResponseBody['domains']>[number];

const hasDomainTag = (account: BankAccountResponseBody) => !isNilOrEmpty(account.domains);

const isDomainTagged = (domain: BankAccountDomain) => (account: BankAccountResponseBody) =>
  account.domains?.includes(domain) ?? false;

const isValidForDomain = (domain: BankAccountDomain) => (account: BankAccountResponseBody) =>
  isNilOrEmpty(account.domains) || isDomainTagged(domain)(account);

/**
 * Returns bank accounts valid for the given domain.
 *
 * When the domain system is active (at least one account carries a domain tag),
 * only accounts tagged with the domain and untagged accounts are returned.
 */
export function getDomainBankAccounts(
  accounts: BankAccountResponseBody[],
  domain: BankAccountDomain
): BankAccountResponseBody[] {
  if (!accounts.some(hasDomainTag)) {
    return accounts;
  }
  return accounts.filter(isValidForDomain(domain));
}

/**
 * Returns default bank account for the given domain.
 *
 * Priority:
 * 1. Currency-matching domain-valid account (if currency provided)
 * 2. Primary domain-tagged account
 * 3. First domain-tagged account
 *
 * Falls back through the same priority for untagged accounts when no domain system is active.
 */
export function getDefaultDomainBankAccount(
  accounts: BankAccountResponseBody[],
  domain: BankAccountDomain,
  currency?: string
): BankAccountResponseBody | undefined {
  const domainAccounts = getDomainBankAccounts(accounts, domain);
  const isTagged = isDomainTagged(domain);

  if (!isNil(currency)) {
    const currencyMatch = find((acc) => acc.currency === currency, domainAccounts);
    if (!isNil(currencyMatch)) {
      return currencyMatch;
    }
  }

  return (
    find((acc) => acc.isPrimary === true && isTagged(acc), domainAccounts) ??
    find(isTagged, domainAccounts) ??
    find((acc) => acc.isPrimary === true, domainAccounts) ??
    head(domainAccounts)
  );
}
